import { useCallback, useEffect, useMemo, useState } from 'react';
import { normalizeString } from '@/utils/stringUtils';
import { useVacationDpBoardQuery } from './data';
import { countDpBuckets, filterDpRows } from '../utils/vacationDpStatus';
import { filterRowsByPeriod } from '../utils/vacationMonthFilter';
import { enrichDpRows, excludeNonVacationContracts, filterRowsByClient, extractClientOptionsFromRows } from '../utils/vacationDpEnrich';
import { useCollaboratorsRealtimeQuery } from '@/features/Collaborators/Data/hooks/useCollaboratorsRealtimeQuery';
import { todayIsoBrt } from '../../../../shared/vacations/dates';
import type { VacationDpFilterKey, VacationDpRow } from '../models/vacationDpBoard.types';

export type DpEmptyReason = 'none' | 'search' | 'month' | 'filter' | 'base';

export function useVacationDpBoard(today?: Date) {
    const { rows, isLoading: boardLoading, error, isAccessDenied } = useVacationDpBoardQuery();
    // O painel só lê collaboratorType, allocationSummary e photoUrl. Sem estas duas opções o hook
    // buscaria um documento de salário por colaborador (~437 leituras) e mais três coleções de
    // catálogo — nada disso aparece nesta tela.
    const { data: collaborators, isLoading: collaboratorsLoading } = useCollaboratorsRealtimeQuery({
        withSalary: false,
        withResources: false,
    });

    // Sem o mapa de colaboradores todo row sai com collaboratorType null, e o filtro de contrato
    // derruba 100% das linhas — a tela mostraria "0 de 437" e a mensagem de lista vazia
    // antes de a lista existir. Só há o que esperar se o painel já trouxe linhas para enriquecer.
    const awaitingCollaborators = rows.length > 0 && collaborators.length === 0;
    const isLoading = boardLoading || collaboratorsLoading || awaitingCollaborators;
    const [activeFilter, setActiveFilter] = useState<VacationDpFilterKey | null>(null);
    const [search, setSearch] = useState('');
    const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
    const [selectedYear, setSelectedYear] = useState<number | null>(() => new Date().getFullYear());
    const [selectedClient, setSelectedClient] = useState<string | null>(null);

    // `new Date()` como valor padrão de parâmetro cria um objeto novo a cada render. Como ele entra
    // nas dependências abaixo, contagem e filtro seriam recalculados sobre a lista inteira em todo
    // render — inclusive a cada tecla digitada na busca. Fixar aqui mantém a identidade estável.
    const referenceDate = useMemo(() => today ?? new Date(), [today]);

    // Enriquecer as linhas com cliente, projeto e tipo de contrato (014.13)
    const enrichedRows = useMemo(() => {
        const collaboratorMap = new Map(
            collaborators.filter((c) => c.id).map((c) => [c.id!, c])
        );
        const enriched = enrichDpRows(rows, collaboratorMap);
        return excludeNonVacationContracts(enriched, todayIsoBrt());
    }, [rows, collaborators]);

    // Os selects de período só aparecem com os cartões de data ativos. Sem este reset, escolher
    // um período, trocar de cartão e esconder os selects deixaria o filtro "grudado" e escondido —
    // o DP veria menos gente do que devia em outro cartão, sem entender por quê.
    useEffect(() => {
        if (activeFilter !== 'onVacation' && activeFilter !== 'scheduled' && activeFilter !== 'vacationsCompleted') {
            setSelectedMonth(null);
            setSelectedYear(null);
        } else if (activeFilter === 'onVacation' || activeFilter === 'scheduled' || activeFilter === 'vacationsCompleted') {
            // Ao entrar num cartão de data com selectores, se o ano estiver null (usuário havia escolhido "Todos"),
            // volta a marcar o ano atual. Permite escolher "Todos" manualmente sem saltar sozinho.
            setSelectedYear((current) => current ?? new Date().getFullYear());
        }
    }, [activeFilter]);

    const counts = useMemo(() => countDpBuckets(enrichedRows, referenceDate), [enrichedRows, referenceDate]);

    const filteredRows = useMemo(
        () => filterDpRows(enrichedRows, activeFilter, referenceDate),
        [enrichedRows, activeFilter, referenceDate]
    );

    // Derivar a chave de filtro a partir de mês e ano
    const periodFilterKey = useMemo(() => {
        if (selectedYear === null) return '';
        if (selectedMonth === null) return String(selectedYear);
        return `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;
    }, [selectedMonth, selectedYear]);

    const monthRows = useMemo(
        () => filterRowsByPeriod(filteredRows, periodFilterKey),
        [filteredRows, periodFilterKey]
    );

    const clientFilteredRows = useMemo(
        () => filterRowsByClient(monthRows, selectedClient),
        [monthRows, selectedClient]
    );

    const visibleRows = useMemo(() => {
        const term = normalizeString(search);
        if (!term) return clientFilteredRows;

        return clientFilteredRows.filter((row: VacationDpRow) =>
            normalizeString(`${row.collaboratorName} ${row.collaboratorEmail}`).includes(term)
        );
    }, [clientFilteredRows, search]);

    const emptyReason = useMemo<DpEmptyReason>(() => {
        if (visibleRows.length > 0) return 'none';
        if (search.trim()) return 'search';
        if (periodFilterKey) return 'month';
        if (activeFilter) return 'filter';
        return 'base';
    }, [visibleRows.length, search, periodFilterKey, activeFilter]);


    const toggleFilter = useCallback((key: VacationDpFilterKey) => {
        setActiveFilter((current) => (current === key ? null : key));
    }, []);

    const clearFilter = useCallback(() => setActiveFilter(null), []);

    const clientOptions = useMemo(() => extractClientOptionsFromRows(enrichedRows), [enrichedRows]);

    const availableYears = useMemo(() => {
        const currentYear = referenceDate.getFullYear();
        const years = new Set<number>([currentYear]);

        const addRange = (startDate: string, endDate: string) => {
            const startYear = parseInt(startDate.slice(0, 4), 10);
            const endYear = parseInt(endDate.slice(0, 4), 10);
            for (let year = Math.max(startYear, currentYear); year <= endYear; year++) {
                years.add(year);
            }
        };

        enrichedRows.forEach((row) => {
            row.approvedPeriods?.forEach((period) => addRange(period.startDate, period.endDate));
            row.request?.segments?.forEach((segment) => addRange(segment.startDate, segment.endDate));
        });

        return Array.from(years).sort((a, b) => a - b);
    }, [enrichedRows, referenceDate]);

    return {
        rows: enrichedRows,
        visibleRows,
        counts,
        totalCount: enrichedRows.length,
        visibleCount: visibleRows.length,
        activeFilter,
        toggleFilter,
        clearFilter,
        search,
        setSearch,
        selectedMonth,
        setSelectedMonth,
        selectedYear,
        setSelectedYear,
        availableYears,
        emptyReason,
        isLoading,
        error,
        isAccessDenied,
        // Novos (014.13)
        selectedClient,
        setSelectedClient,
        clientOptions,
    };
}
